import { Theme } from "../../../../providers/ThemeProvider";
import useTheme from "../../../../common/hooks/useTheme";
import { Button, Checkbox, ListItemText, MenuItem } from "@mui/material";
import React from "react";
import { chartKeys } from "../../store/helper";
import { CustomCheckBox } from "./ChartKeyPicker";
import ScrollableWrapper from "../common/ScrollableWrapper";

type FilterPickerProps = {
  data: string[];
  title?: string;
  selectedKeys: string[];
  onSelectionChange: (selected: string[]) => void;
  options?: Array<{ key: string; label: string; origin?: string }>;
  showOriginLogic?: boolean;
  /**
   * Note under the title, for a picker whose selection is constrained — the
   * breath chart's four-line cap, say. Omitted renders nothing, so every
   * existing caller is unaffected.
   */
  subtitle?: string;
};

const FilterPicker = ({
  data,
  title = "Filters",
  selectedKeys,
  onSelectionChange,
  options,
  showOriginLogic = false,
  subtitle,
}: FilterPickerProps) => {
  const theme: Theme = useTheme();

  // Clear all selections
  const handleClearAll = () => {
    onSelectionChange([]);
  };

  // Filter data based on whether we're using origin logic or not
  const filteredData = showOriginLogic
    ? data
    : data.filter(
        (item) =>
          item !== "heart-rate-variability" &&
          item !== "sleep-heart-rate-variability"
      );

  // Get label for display - either from options or chartKeys
  const getItemLabel = (keyValue: string) => {
    if (options) {
      const option = options.find((opt) => opt.key === keyValue);
      if (option) return option.label;
    }

    // Fallback to chartKeys lookup
    const chartOption = chartKeys.find((item) => item.key === keyValue);
    return chartOption?.label || keyValue;
  };

  return (
    <div style={styles.container(theme)}>
      <div style={styles.filtersHeader(theme)}>
        <div style={styles.filtersTitleBlock}>
          <div style={styles.filtersTitle(theme)}>{title}</div>
          {subtitle && (
            <div style={styles.filtersSubtitle(theme)}>{subtitle}</div>
          )}
        </div>
        <Button sx={styles.clearButton(theme)} onClick={handleClearAll}>
          Clear
        </Button>
      </div>
      <ScrollableWrapper>
        {filteredData.map((keyValue) => {
          const itemLabel = getItemLabel(keyValue);

          // Skip if no label found for non-origin logic and no options provided
          if (
            !showOriginLogic &&
            !options &&
            !chartKeys.find((item) => item.key === keyValue)
          ) {
            return null;
          }

          return (
            <MenuItem
              key={keyValue}
              style={styles.menuItem}
              onClick={() => {
                const newSelected = selectedKeys.includes(keyValue)
                  ? selectedKeys.filter((key) => key !== keyValue)
                  : [...selectedKeys, keyValue];
                onSelectionChange(newSelected);
              }}
              value={keyValue}
            >
              <CustomCheckBox selected={selectedKeys.indexOf(keyValue) > -1} />
              <div style={styles.menuItemLabel(theme)}>{itemLabel}</div>
            </MenuItem>
          );
        })}
      </ScrollableWrapper>
    </div>
  );
};

const styles = {
  container: (theme: Theme) => ({
    width: "300px",
    maxHeight: "400px",
    display: "flex",
    flexDirection: "column" as "column",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    padding: "16px",
    borderRadius: "8px",
  }),
  filtersHeader: (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingBottom: "8px",
    marginBottom: "16px",
    borderBottom: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
  }),
  filtersTitleBlock: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "2px",
    minWidth: 0,
  },
  filtersTitle: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  filtersSubtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "10px",
    lineHeight: "1.4",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
  }),
  clearButton: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.PRIMARY_TEXT_MAIN,
    textTransform: "none" as "none",
    padding: "0",
    "&:hover": {
      backgroundColor: "transparent",
    },
  }),
  menuItem: {
    padding: "4px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
  },
  message: (theme: Theme) => ({
    fontFamily: "var(--font-Regular)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    textAlign: "center" as "center",
  }),
  menuItemLabel: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    flex: 1,
    whiteSpace: "normal",
    wordBreak: "break-word" as "break-word",
  }),
};

export default FilterPicker;
